/**
 * Lead follow-ups (the phone's Follow-ups feed) + Done/Undo — 2026-10-07,
 * Manak option 1. Same backend calls as the AURA Sales app:
 *   GET  /lead-integrations/follow-ups
 *   POST /lead-integrations/lead-imports/:id/follow-up-done
 *   POST /lead-integrations/lead-imports/:id/follow-up-undo { followUpDate }
 */
import { api } from '@/utils/api';
import { joinPersonName } from '@/utils/names';

export interface LeadFollowUpRow {
  id: string; // LeadImport id
  contactName?: string | null;
  phone?: string | null;
  scheduledAt: string | null;
  status: string;
  canonicalStage?: string | null;
  isWon?: boolean;
  notes?: string | null;
  contact?: { firstName?: string | null; lastName?: string | null; phone?: string | null; mobile?: string | null } | null;
}

/** How long the Undo toast stays up. */
export const UNDO_MS = 5000;

/** Counts for the page header — the same numbers the My Day card shows. */
export interface FollowUpsSummary {
  scheduled: number;
  overdue: number;
  needDate: number;
}

export async function fetchLeadFollowUps(): Promise<LeadFollowUpRow[]> {
  return (await fetchLeadFollowUpsFeed()).rows;
}

/**
 * Rows + the server's counts (2026-10-07, tour bugs 1+2). The server counts
 * with real COUNT queries on the same list My Day uses, so the header and the
 * dashboard card agree even past the 500-row list cap. Old servers send no
 * summary → counted from the rows.
 */
export async function fetchLeadFollowUpsFeed(now: number = Date.now()): Promise<{ rows: LeadFollowUpRow[]; summary: FollowUpsSummary }> {
  const res = await api.get('/lead-integrations/follow-ups');
  if (res.data?.success === false) throw new Error(res.data?.error || 'Could not load follow-ups');
  const rows = (Array.isArray(res.data?.data) ? res.data.data : []) as LeadFollowUpRow[];
  const s = res.data?.summary;
  const ok = s && [s.scheduled, s.overdue, s.needDate].every((n: unknown) => Number.isInteger(n) && (n as number) >= 0);
  return { rows, summary: ok ? { scheduled: s.scheduled, overdue: s.overdue, needDate: s.needDate } : summarizeRows(rows, now) };
}

/** Counts from the rows themselves (fallback). Overdue = due before now. */
export function summarizeRows(rows: LeadFollowUpRow[], now: number): FollowUpsSummary {
  const pending = rows.filter((r) => r.status === 'PENDING');
  const dated = pending.filter((r) => !!r.scheduledAt);
  return {
    scheduled: dated.length,
    overdue: dated.filter((r) => new Date(r.scheduledAt as string).getTime() < now).length,
    needDate: pending.length - dated.length,
  };
}

/** "201 scheduled · 146 overdue · 9 need a date" (the last part only when > 0). */
export function followUpsSummaryText(s: FollowUpsSummary): string {
  const parts = [`${s.scheduled} scheduled`, `${s.overdue} overdue`];
  if (s.needDate > 0) parts.push(`${s.needDate} need${s.needDate === 1 ? 's' : ''} a date`);
  return parts.join(' · ');
}

/** Returns the cleared date (ISO) for Undo. */
export async function markFollowUpDone(leadId: string): Promise<string | null> {
  const res = await api.post(`/lead-integrations/lead-imports/${leadId}/follow-up-done`);
  return res.data?.data?.previousFollowUpDate ?? null;
}

export async function undoFollowUpDone(leadId: string, followUpDate: string): Promise<void> {
  await api.post(`/lead-integrations/lead-imports/${leadId}/follow-up-undo`, { followUpDate });
}

export function isWonRow(r: LeadFollowUpRow): boolean {
  return r.isWon === true || (r.canonicalStage ?? '').toLowerCase() === 'won';
}

/**
 * 2026-10-07 (tour bug 3) — the lead's own name first (`contactName` is the
 * lead name from the server; the Leads list shows the same), the linked
 * contact's name only when the lead has none. It was the other way round, so
 * the same person showed one name here and another on Leads / the phone.
 */
export function rowName(r: LeadFollowUpRow): string {
  const joined = joinPersonName(r.contact?.firstName, r.contact?.lastName).trim();
  return (r.contactName ?? '').trim() || joined || (r.phone ?? '').trim() || 'Contact name not recorded';
}

const SHORT_DATE = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });

export function wonFollowUpPromptText(name: string, followUpDateIso: string): string {
  return `${name} has a follow-up due on ${SHORT_DATE.format(new Date(followUpDateIso))}. Mark that follow-up as done?`;
}
